// Typed wrapper around the Rust backend's HTTP API. Matches the routes
// in `backend/src/router.rs`. The backend serves us same-origin at /api
// in prod; in dev Vite proxies /api to 127.0.0.1:3030.

import type {
  ActionBuildResponse,
  ActionDescriptor,
  ApiError,
  ApiErrorBody,
  CheckRelayerResponse,
  ConnectorId,
  ConnectorInfo,
  HealthResponse,
  PauseStatusResponse,
  UnsignedTxsResponse,
} from "@/types/dto";

export class ApiClientError extends Error {
  /** HTTP status from the response, or 0 for a network-level failure. */
  status: number;
  /** Backend's `ApiErrorCode` (SCREAMING_SNAKE) when the response parsed as
   *  ApiErrorBody, or `"NETWORK"` / `"DECODE"` for client-side failures. */
  code: string;
  details?: unknown;

  constructor(message: string, status: number, code: string, details?: unknown) {
    super(message);
    this.name = "ApiClientError";
    this.status = status;
    this.code = code;
    this.details = details;
  }

  /** Was this a `NeedsExternalProof` 422 the backend uses to redirect the
   *  operator to bridge-cli for an action it can't build today? */
  get needsBridgeCli(): boolean {
    return this.code === "NEEDS_EXTERNAL_PROOF";
  }
}

async function parse<T>(resp: Response): Promise<T> {
  const text = await resp.text();
  if (!resp.ok) {
    // 4xx/5xx — the backend's `BackendError::IntoResponse` always
    // returns an ApiErrorBody envelope (`backend/src/error.rs`). Anything
    // else (proxy 502 with an HTML page, gateway timeout, etc.) falls
    // through to the bare `"HTTP"` envelope so the user still gets an
    // actionable status code instead of a parser crash.
    let parsed: unknown = undefined;
    try {
      parsed = JSON.parse(text);
    } catch {
      // Body wasn't JSON — fall through to `"HTTP"`.
    }
    if (isApiErrorBody(parsed)) {
      const err: ApiError = parsed.error;
      throw new ApiClientError(err.message, resp.status, err.code, err.details);
    }
    throw new ApiClientError(text || resp.statusText, resp.status, "HTTP");
  }
  try {
    return JSON.parse(text) as T;
  } catch (e) {
    throw new ApiClientError(
      `failed to decode response: ${(e as Error).message}`,
      resp.status,
      "DECODE",
    );
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    !!value &&
    typeof value === "object" &&
    "error" in value &&
    !!(value as { error?: unknown }).error &&
    typeof (value as { error?: { code?: unknown } }).error === "object" &&
    typeof (value as { error: { code?: unknown } }).error.code === "string" &&
    typeof (value as { error: { message?: unknown } }).error.message === "string"
  );
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(path, { method: "GET", signal });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiClientError((e as Error).message, 0, "NETWORK");
  }
  return parse<T>(resp);
}

async function post<T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> {
  let resp: Response;
  try {
    resp = await fetch(path, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
      signal,
    });
  } catch (e) {
    if ((e as Error).name === "AbortError") throw e;
    throw new ApiClientError((e as Error).message, 0, "NETWORK");
  }
  return parse<T>(resp);
}

// ─── Endpoints ──────────────────────────────────────────────────────────

export function health(signal?: AbortSignal): Promise<HealthResponse> {
  return get<HealthResponse>("/api/health", signal);
}

export function listConnectors(signal?: AbortSignal): Promise<ConnectorInfo[]> {
  return get<ConnectorInfo[]>("/api/connectors", signal);
}

export function checkRelayer(accountId: string, signal?: AbortSignal) {
  return get<CheckRelayerResponse>(
    `/api/check-relayer/${encodeURIComponent(accountId)}`,
    signal,
  );
}

export function actionSchema(signal?: AbortSignal): Promise<ActionDescriptor[]> {
  return get<ActionDescriptor[]>("/api/actions/schema", signal);
}

/** Generic NEAR view-function pass-through. The backend proxies the call
 *  to its NEAR RPC client so the browser never sees the operator's RPC
 *  URL. Returns whatever JSON shape the contract gave; callers parse it
 *  into a typed view shape. Used by the proof-based flows for
 *  storage-deposit derivation (`get_token_id`, `storage_balance_bounds`,
 *  …) and UTXO connector state. */
export async function nearView<T = unknown>(
  contract: string,
  method: string,
  args: unknown = {},
  signal?: AbortSignal,
): Promise<T> {
  const resp = await post<{ result: T }>(
    "/api/near/view",
    { contract, method, args },
    signal,
  );
  return resp.result;
}

/** Scrape a NEAR→UTXO `init_transfer` tx for the details
 *  `submit_transfer_to_utxo_chain_connector` needs (transfer id, target UTXO
 *  address, net amount, optional max gas fee). The frontend has no NEAR
 *  `tx_status` RPC, so the backend does the scrape. */
export interface UtxoWithdrawalInfo {
  origin_chain: string;
  origin_nonce: number;
  target_address: string;
  net_amount: string;
  max_gas_fee?: string;
}
export function utxoWithdrawalInfo(
  nearTxHash: string,
  senderId: string,
  signal?: AbortSignal,
): Promise<UtxoWithdrawalInfo> {
  return post<UtxoWithdrawalInfo>(
    "/api/near/utxo-withdrawal-info",
    { near_tx_hash: nearTxHash, sender_id: senderId },
    signal,
  );
}

/** Scrape a NEAR `sign_transfer` tx for the MPC signature + transfer payload
 *  that destination-side `fin_transfer` (EVM / Solana / Starknet) needs. The
 *  browser can't call NEAR `tx_status`, so the backend reads the
 *  `SignTransferEvent` from the receipt; the frontend feeds the result to
 *  `@omni-bridge/{evm,solana,starknet}` `buildFinalization`. */
export interface SignTransferPayload {
  signature: { big_r: { affine_point: string }; s: { scalar: string }; recovery_id: number };
  message_payload: {
    prefix?: string;
    destination_nonce: number | string;
    transfer_id: { origin_chain: string; origin_nonce: number | string };
    token_address: string;
    amount: string;
    recipient: string;
    fee_recipient?: string | null;
    message?: string | null;
  };
}
export function signTransferPayload(
  nearTxHash: string,
  senderId: string,
  signal?: AbortSignal,
): Promise<SignTransferPayload> {
  return post<SignTransferPayload>(
    "/api/near/sign-transfer-payload",
    { near_tx_hash: nearTxHash, sender_id: senderId },
    signal,
  );
}

export function buildAction(
  slug: string,
  body: unknown,
  signal?: AbortSignal,
): Promise<ActionBuildResponse> {
  return post<ActionBuildResponse>(
    `/api/actions/${encodeURIComponent(slug)}/build`,
    body,
    signal,
  );
}

export function pauseStatus(
  connector: ConnectorId,
  signal?: AbortSignal,
): Promise<PauseStatusResponse> {
  return get<PauseStatusResponse>(
    `/api/connectors/${connector}/pause-status`,
    signal,
  );
}

export function pauseFeature(
  connector: ConnectorId,
  feature: string,
  signal?: AbortSignal,
): Promise<UnsignedTxsResponse> {
  return post<UnsignedTxsResponse>(
    `/api/connectors/${connector}/pause/${encodeURIComponent(feature)}`,
    {},
    signal,
  );
}

export function unpauseFeature(
  connector: ConnectorId,
  feature: string,
  signal?: AbortSignal,
): Promise<UnsignedTxsResponse> {
  return post<UnsignedTxsResponse>(
    `/api/connectors/${connector}/unpause/${encodeURIComponent(feature)}`,
    {},
    signal,
  );
}
